/*
Authentication service with JWT.
*/
import api from './api'
import { saveAuthData, clearAuthData, getStoredUser } from './authStorage.js'

export const login = async (email, password) => {
  // An old Django session can survive local sign-out; always bootstrap CSRF.
  const csrf = await api.get('/auth/csrf/', { withCredentials: true })
  const response = await api.post('/auth/login/', { email, password }, {
    withCredentials: true,
    headers: { 'X-CSRFToken': csrf.data.csrfToken },
  })

  // dj-rest-auth with JWT returns keys directly
  const { access, refresh, user } = response.data
  saveAuthData({ access, refresh, user })

  return user
}

export const register = async (userData) => {
  // dj-rest-auth registration endpoint
  const response = await api.post('/auth/registration/', userData)

  // Usually returns access/refresh if email verification is not mandatory,
  // OR just a success message if mandatory.
  // Since we set ACCOUNT_EMAIL_VERIFICATION = 'mandatory', it might not return tokens immediately.
  // But let's handle standard response or "Check email" flow.
  // If 'access' token is present, login. If not, return user data or message.

  if (response.data.access) {
    saveAuthData(response.data)
  }

  return response.data
}

export const logout = () => {
  clearAuthData()

  // Also hit backend logout if using session/cookies?
  // dj-rest-auth /logout/ handles this usually.
  return api.post('/auth/logout/').catch(() => { })
}

export const getCurrentUser = async () => {
  const response = await api.get('/auth/user/')
  localStorage.setItem('user', JSON.stringify(response.data))
  return response.data
}

export const isAuthenticated = () => {
  return !!localStorage.getItem('access_token') || !!localStorage.getItem('user_session_active')
}

export const checkAuth = async () => {
  // Only probe server if we have a token (JWT) or a session flag (Cookie flow)
  // OR if we are handling a redirect from AllAuth (signaled by confirmed=true or similar params)
  // This prevents 401 probes for purely anonymous users
  const hasToken = !!localStorage.getItem('access_token')
  const hasSessionFlag = !!localStorage.getItem('user_session_active')
  const isRedirect = window.location.hash.includes('confirmed=') || window.location.search.includes('confirmed=')

  if (!hasToken && !hasSessionFlag && !isRedirect) {
    return null // Assume anonymous without probing
  }

  try {
    const user = await getCurrentUser()
    localStorage.setItem('user_session_active', 'true')
    return user
  } catch (e) {
    clearAuthData()
    throw e
  }
}

export const getUserRole = () => getStoredUser()?.profile?.role || 'user'

export const isAdmin = () => getUserRole() === 'admin'

export const getUser = getStoredUser

export const passwordReset = async (email) => {
  return await api.post('/auth/password/reset/', { email })
}

export const passwordResetConfirm = async (uid, token, newPassword1, newPassword2) => {
  return await api.post('/auth/password/reset/confirm/', {
    uid,
    token,
    new_password1: newPassword1,
    new_password2: newPassword2
  })
}

export const resendVerification = async (email) => {
  return await api.post('/auth/registration/resend-email/', { email })
}
